import { ReactComponent as CheckDefIcon } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckClearIcon } from "assets/images/icons/checkbox_clear.svg";
import { ReactComponent as CheckFillIcon } from "assets/images/icons/checkbox_fiilled.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Checkbox,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from 'uuid';

// Hook
import useDataSourceManager from 'hooks/useDataSourceManager';

// Component
import SearchInput from "components/SearchInput";
import PaginationField from 'components/Pagination';
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";

// Util
import localStorageUtil from "utils/localStorageUtil";

const FromClientView = ({
  auth,
  viewStateEnum,
  getViewTitle,
  form,
  handleFromClientView,
  showEditGeneral,
}) => {
  const theme = useTheme();
  const viewRef = useRef(null);
  const tableName = "fromClientMacAddressTable";
  const { dataMap, getData, sendRequest } = useDataSourceManager();

  // Status
  const [clientsUuid, _setClientsUuid] = useState(uuidv4());
  const [searchVal, setSearchVal] = useState("");
  const [originalMacAddressItemList, setOriginalMacAddressItemList] = useState([]);
  const [macAddressItemList, setMacAddressItemList] = useState([]);
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(localStorageUtil.getItemField('resultsPerPageObject', tableName) || 10);

  // Method
  const fetchClientDeviceList = () => {
    const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

    const apiConfig = {
      org: `${baseUrl}/clients.read`,
      site: `${baseUrl}/sites/:siteId/clients.read`,
    };

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
    };

    const apiKey = apiConfig[auth.scopeLevel];
    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    sendRequest(apiKey, clientsUuid, restfulParams);
  }

  const closeView = () => {
    handleFromClientView.closeView();
  }

  const filterMacAddressList = (searchValue) => {
    setSearchVal(searchValue);
    setSelectedIndexes([]);

    if (!searchValue || searchValue.trim() === "") {
      setMacAddressItemList(originalMacAddressItemList);
      return;
    }

    const filteredList = originalMacAddressItemList.filter(item =>
      item.macAddress.toLowerCase().includes(searchValue.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchValue.toLowerCase()))
    );

    setMacAddressItemList(filteredList);
  }

  const handleApply = () => {
    const selectedMacAddressItems = selectedIndexes.map(index => macAddressItemList[index]);
    handleFromClientView.handleApply(selectedMacAddressItems);
    closeView();
  }

  // Side Effects
  useEffect(() => {
    fetchClientDeviceList();
    // setMacAddressItemList([]);
    // const tempList = [];
    // for (let i = 0; i < 50; i++) {
    //   const mac = (i + 10).toString(16).toUpperCase().padStart(2, '0');
    //   const macAddress = `AA:AA:AA:AA:AA:${mac}`;
    //   tempList.push({
    //     macAddress,
    //     description: `Client Device ${i + 1}`,
    //   });
    // }
    // setMacAddressItemList(tempList);
  }, []);

  useEffect(() => {
    const clientsResponse = getData(clientsUuid);
    if (clientsResponse && clientsResponse.data) {
      const updatedMacAddressList = clientsResponse.data.map(client => ({
        macAddress: client.clientId,
        description: client.clientName || '',
      }));

      setOriginalMacAddressItemList(updatedMacAddressList);
      setMacAddressItemList(updatedMacAddressList);
    }
  }, [dataMap]);

  useEffect(() => {
    setCurrentPage(0);
  }, [rowsPerPage]);

  return (
    <>
      <Box
        ref={viewRef}
        style={{
          position: "relative",
          padding: '0',
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              paddingLeft: "20px",
            }}
          >
            {getViewTitle(viewStateEnum.FROM_CLIENT)}
          </Typography>
          <Button
            onClick={closeView}
            sx={{
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>

        <Box
          style={{
            marginTop: '20px',
            marginLeft: '10px',
            marginRight: '10px',
            marginBottom: '10px',
          }}
        >
          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '10px',
              paddingLeft: '10px',
            }}
          >
            <SearchInput value={searchVal} onChange={filterMacAddressList} />
          </Box>
        </Box>

        <Box
          style={{
            border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
            borderRadius: '4px',
            margin: '0 10px 0 20px',
          }}
        >
          <Box sx={{ ...form.getTableStyle() }}>
            <TableContainer
              component={Paper}
              sx={{
                "&.MuiPaper-root": {
                  boxShadow: "none",
                  border: 'none',
                },
              }}
              style={{ height: '300px', overflowY: 'auto' }}
            >
              <Table
                sx={{
                  borderCollapse: "collapse",
                  "& td, & th": {
                    border: "none",
                  },
                  "& tbody tr:nth-of-type(odd)": {
                    backgroundColor: 'rgb(246, 246, 246)',
                  },
                  "& tbody tr:nth-of-type(even)": {
                    backgroundColor: theme.palette.primary.white,
                  },
                }}
              >
                <TableHead>
                  <TableRow>

                    {
                      showEditGeneral && (
                        <>
                          <TableCell>
                            <Checkbox
                              icon={<CheckDefIcon style={{ width: "25px", height: "25px" }} />}
                              checkedIcon={<CheckFillIcon style={{ width: "25px", height: "25px" }} />}
                              indeterminateIcon={<CheckClearIcon style={{ width: "25px", height: "25px" }} />}
                              indeterminate={
                                selectedIndexes.length > 0 &&
                                selectedIndexes.length < macAddressItemList.length
                              }
                              checked={
                                macAddressItemList.length > 0 &&
                                selectedIndexes.length === macAddressItemList.length
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedIndexes(macAddressItemList.map((_, index) => index));
                                } else {
                                  setSelectedIndexes([]);
                                }
                              }}
                            />
                          </TableCell>
                        </>
                      )
                    }
                    <TableCell sx={{ fontWeight: 600 }}>MAC Address</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    macAddressItemList.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          sx={{
                            textAlign: "center",
                            padding: "20px",
                          }}
                        >
                          No Entry in the table
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {
                          macAddressItemList.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage)
                            .map((mac, index) => (
                              <>
                                <TableRow key={index}>
                                  {
                                    showEditGeneral && (
                                      <>
                                        <TableCell>
                                          <Checkbox
                                            icon={<CheckDefIcon style={{ width: "25px", height: "25px" }} />}
                                            checkedIcon={<CheckFillIcon style={{ width: "25px", height: "25px" }} />}
                                            checked={selectedIndexes.includes(index)}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setSelectedIndexes([...selectedIndexes, index]);
                                              } else {
                                                setSelectedIndexes(selectedIndexes.filter(i => i !== index));
                                              }
                                            }}
                                          />
                                        </TableCell>
                                      </>
                                    )
                                  }
                                  <TableCell>{mac.macAddress}</TableCell>
                                  <TableCell>{mac.description}</TableCell>
                                </TableRow>
                              </>
                            ))
                        }
                      </>
                    )
                  }
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box mt={2} mb={2} display="flex" justifyContent="center">
            <PaginationField
              name={tableName}
              pageCount={macAddressItemList?.length > 0 ? Math.max(1, Math.ceil((macAddressItemList?.length ?? 0) / rowsPerPage)) : 0}
              pageIndex={currentPage}
              pageSize={rowsPerPage}
              onPageChange={page => setCurrentPage(page)}
              onPageSizeChange={size => {
                setCurrentPage(0);
                setRowsPerPage(size)
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            margin: "24px 0 16px auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <OutlineButton
            onClick={closeView}
            sx={{ marginRight: "20px" }}
            width="100px"
            height="32px"
          >
            Cancel
          </OutlineButton>
          <SolidButton
            onClick={handleApply}
            width="100px"
            height="32px"
            disabled={selectedIndexes.length === 0}
          >
            Apply {selectedIndexes.length > 0 && <Box style={form.getNumberStyle(false)}>{selectedIndexes.length}</Box>}
          </SolidButton>
        </Box>
      </Box>
    </>
  )
}

export default React.memo(FromClientView);